interface UserTrialSettings {
  collect_payment_method?: boolean
  trial_end_behavior?: 'pause' | 'cancel'
}

export const useTrialConfig = (overrides?: {
  enableFreeTrial?: boolean
  trialPeriodDays?: number
  collectPaymentMethod?: boolean
  trialEndBehavior?: 'cancel' | 'pause'
}) => {
  const config = useRuntimeConfig()
  const user = useSupabaseUser()
  
  // User-specific trial settings (reactive)
  const userTrialSettings = ref<UserTrialSettings | null>(null)
  
  // Fetch user-specific trial settings
  const fetchUserTrialSettings = async () => {
    if (!user.value?.id) return
    
    try {
      const response = await $fetch<{ data: UserTrialSettings }>(`/api/users/${user.value.id}/trial-settings`)
      userTrialSettings.value = response.data
    } catch (error) {
      // User settings not found or error - will fall back to config defaults
      userTrialSettings.value = null
    }
  }
  
  // Watch for user changes
  watch(() => user.value?.id, () => {
    if (user.value?.id) {
      fetchUserTrialSettings()
    } else {
      userTrialSettings.value = null
    }
  }, { immediate: true })

  const isTrialEnabled = computed(() => {
    // Priority: component override > environment config
    return overrides?.enableFreeTrial ?? (config.public.trials as any)?.enabled ?? false
  })

  const trialPeriodDays = computed(() => {
    // Priority: component override > environment config
    return overrides?.trialPeriodDays ?? (config.public.trials as any)?.defaultPeriodDays ?? 30
  })

  const shouldCollectPaymentMethod = computed(() => {
    // Priority: component override > user setting > environment config
    return overrides?.collectPaymentMethod ?? 
           userTrialSettings.value?.collect_payment_method ?? 
           (config.public.trials as any)?.collectPaymentMethod ?? 
           false
  })

  const trialEndBehavior = computed(() => {
    // Priority: component override > user setting > environment config
    return overrides?.trialEndBehavior ?? 
           userTrialSettings.value?.trial_end_behavior ?? 
           (config.public.trials as any)?.endBehavior ?? 
           'pause'
  })

  return {
    isTrialEnabled: readonly(isTrialEnabled),
    trialPeriodDays: readonly(trialPeriodDays),
    shouldCollectPaymentMethod: readonly(shouldCollectPaymentMethod),
    trialEndBehavior: readonly(trialEndBehavior),
    userTrialSettings: readonly(userTrialSettings),
    refreshUserSettings: fetchUserTrialSettings
  }
} 